import { handleError } from "@ch/helpers/utils/handleError";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { apiWrapper } from ".";

type ServiceFunction<A, B> = (payload: A) => Promise<B>;

interface UseGetRequestOptions<A, B> {
  service: ServiceFunction<A, B>;
  payload: A;
  page?: number;
  limit?: number;
  searchText?: string;
  enabled?: boolean;
  onSuccess?: (val?: any) => void;
  onError?: (val?: any) => void;
  tag: string;
  refetchInterval?: number | false;
}

interface UseGetRequestResult<B> {
  loading: boolean;
  isLoading: boolean;
  data?: B;
  onUpdateCallback?: (val: any, key?: string) => void;
  onAddCallback?: (val: B) => void;
  error?: any;
  refetch?: () => void;
  isRefetching?: boolean;
  isFetching?: boolean;
  status?: string;
}

export function useGetRequest<A, B>({
  service,
  payload,
  onError,
  onSuccess,
  tag,
  enabled = true,
  refetchInterval = false,
}: UseGetRequestOptions<A, B>): UseGetRequestResult<B> {
  const methodName = service.name;
  const queryKey = [tag, { methodName, payload }];

  const info = useQuery<B, unknown>({
    queryKey,
    queryFn: async () => {
      const data = await apiWrapper(() => service(payload));
      if (onSuccess) onSuccess(data);
      return data;
    },
    enabled,
    refetchInterval,
  });

  useEffect(() => {
    if (info.isError) {
      handleError(info.error);
      onError?.(info.error);
    }
  }, [info.isError, info.error]);

  return {
    loading: info.isLoading,
    isLoading: info.isLoading,
    data: info.data,
    error: info.error,
    refetch: info.refetch,
    isRefetching: info.isRefetching || info.isFetching,
    isFetching: info.isFetching,
    status: info.status,
  };
}
